/* eslint-disable no-magic-numbers */
import { useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import {
  addReactionAnnotation,
  deleteReactionAnnotation,
  getReactionsForModel,
  updateReactionMeta,
  updateReactionAnnotation,
} from '@/redux/reactions/reactions.thunks';
import { showToast } from '@/utils/showToast';
import { reactionMetaStateSelector } from '@/redux/modal/modal.selector';
import { modelElementsByModelIdSelector } from '@/redux/modelElements/modelElements.selector';
import { reactionsByModelIdSelector } from '@/redux/reactions/reactions.selector';
import { Annotation } from '@/types/models';
import { UpdateReactionData } from '@/redux/reactions/reactions.types';
import { isReducedNotationReaction } from '@/utils/reaction/reducedNotationReaction';

const DEFAULT_Z = 0;

let annotationIdCounter = -1;

const createEmptyAnnotation = (): Annotation => {
  const id = annotationIdCounter;
  annotationIdCounter -= 1;
  return { id, link: null, article: null, type: '', resource: '', annotator: null };
};

/**
 * Owns the editable reaction-level state (name, description, reversibility,
 * stacking order, annotations) plus the auto-save flow shared by every control
 * in {@link ReactionPropertiesForm}. Line styling is handled separately by
 * {@link useReactionLines}, because each line has its own endpoint.
 *
 * Mirrors `useElementProperties`: each control updates its local state and
 * calls `save({ field })` with the new value so the write never races the
 * (async) state update.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useReactionProperties = () => {
  const dispatch = useAppDispatch();

  const reactionMetaState = useAppSelector(reactionMetaStateSelector);
  const modelId = reactionMetaState?.model;
  const reactions = useAppSelector(state => reactionsByModelIdSelector(state, modelId || 0));
  const modelElements = useAppSelector(state =>
    modelElementsByModelIdSelector(state, modelId || 0),
  );
  const reaction = reactions.find(item => item.id === reactionMetaState?.id);
  const reactionId = reaction?.id;

  const [isSending, setIsSending] = useState<boolean>(false);

  const [name, setName] = useState<string>(reaction?.name || '');
  const [notes, setNotes] = useState<string>(reaction?.notes || '');
  const [reversible, setReversible] = useState<boolean>(reaction?.reversible ?? false);
  const [z, setZ] = useState<number>(reaction?.z ?? DEFAULT_Z);
  const [annotations, setAnnotations] = useState<Annotation[]>(reaction?.references || []);

  const save = async (overrides: Partial<UpdateReactionData> = {}): Promise<void> => {
    if (!reaction || modelId === undefined) {
      return;
    }
    setIsSending(true);
    try {
      await dispatch(
        updateReactionMeta({
          reaction,
          data: { name, notes, reversible, z, ...overrides },
        }),
      ).unwrap();
      dispatch(getReactionsForModel(modelId));
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while updating the reaction.',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Annotations have dedicated create/update/delete endpoints,
  // separate from the reaction PUT above. A row with a negative id is a local
  // draft not yet persisted; a positive id is a saved annotation. After any
  // server write we re-read the reaction so drafts pick up their real id/link.
  const syncAnnotationsFromServer = async (): Promise<void> => {
    if (modelId === undefined || reactionId === undefined) {
      return;
    }
    const updatedReactions = await dispatch(getReactionsForModel(modelId)).unwrap();
    const updated = updatedReactions?.find(item => item.id === reactionId);
    if (updated) {
      setAnnotations(updated.references);
    }
  };

  // Creates a draft, updates an existing row; incomplete rows are left untouched.
  const persistAnnotation = async (annotation: Annotation): Promise<void> => {
    if (modelId === undefined || reactionId === undefined) {
      return;
    }
    const annotationData = { type: annotation.type.trim(), resource: annotation.resource.trim() };
    if (!annotationData.type || !annotationData.resource) {
      return;
    }
    setIsSending(true);
    try {
      if (annotation.id < 0) {
        await dispatch(addReactionAnnotation({ modelId, reactionId, annotationData })).unwrap();
      } else {
        await dispatch(
          updateReactionAnnotation({
            modelId,
            reactionId,
            annotationId: annotation.id,
            annotationData,
          }),
        ).unwrap();
      }
      await syncAnnotationsFromServer();
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while saving the annotation.',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleAnnotationChange = (
    index: number,
    key: keyof Pick<Annotation, 'type' | 'resource'>,
    value: string,
  ): void => {
    setAnnotations(prev =>
      prev.map((annotation, i) => (i === index ? { ...annotation, [key]: value } : annotation)),
    );
  };

  const handleAddAnnotation = (): void => {
    setAnnotations(prev => [...prev, createEmptyAnnotation()]);
  };

  // The resource is a free-text field, so it persists on blur.
  const handleAnnotationBlur = (index: number): void => {
    const annotation = annotations[index];
    if (annotation) {
      persistAnnotation(annotation);
    }
  };

  // The type is a dropdown (no blur), so persist right after selection.
  const handleAnnotationTypeChange = (index: number, type: string): void => {
    const updated = annotations.map((annotation, i) =>
      i === index ? { ...annotation, type } : annotation,
    );
    setAnnotations(updated);
    persistAnnotation(updated[index]);
  };

  const handleRemoveAnnotation = async (index: number): Promise<void> => {
    const annotation = annotations[index];
    if (!annotation) {
      return;
    }
    // A draft that was never persisted is just dropped locally.
    if (annotation.id < 0) {
      setAnnotations(prev => prev.filter((_annotation, i) => i !== index));
      return;
    }
    if (modelId === undefined || reactionId === undefined) {
      return;
    }
    setIsSending(true);
    try {
      await dispatch(
        deleteReactionAnnotation({ modelId, reactionId, annotationId: annotation.id }),
      ).unwrap();
      await syncAnnotationsFromServer();
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while deleting the annotation.',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Stacking is computed across every element and reaction on the same diagram.
  const handleBringToFront = (): void => {
    const maxZ = [...modelElements, ...reactions].reduce(
      (max, element) => Math.max(max, element.z),
      z,
    );
    const newZ = maxZ + 1;
    setZ(newZ);
    save({ z: newZ });
  };

  const handleSendToBack = (): void => {
    const minZ = [...modelElements, ...reactions].reduce(
      (min, element) => Math.min(min, element.z),
      z,
    );
    const newZ = minZ - 1;
    setZ(newZ);
    save({ z: newZ });
  };

  const handleToggleReversible = (checked: boolean): void => {
    setReversible(checked);
    save({ reversible: checked });
  };

  // Reduced notation (Activity Flow) reactions describe an influence, not a
  // transformation, so reversibility does not apply to them at all.
  const isReversibleApplicable = !isReducedNotationReaction(reaction?.sboTerm || '');

  return {
    reaction,
    isSending,
    save,
    name,
    setName,
    notes,
    setNotes,
    reversible,
    isReversibleApplicable,
    handleToggleReversible,
    z,
    annotations,
    handleAnnotationChange,
    handleAnnotationBlur,
    handleAnnotationTypeChange,
    handleAddAnnotation,
    handleRemoveAnnotation,
    handleBringToFront,
    handleSendToBack,
  };
};
